import React, { useEffect } from "react";

import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers";
import { formatISO } from "date-fns";
import { Controller, SubmitHandler } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import {
  ClientWaterIntakeFragment,
  CreateWaterIntakeInput,
  UpdateWaterIntakeInput,
} from "@/gql/graphql";
import useFormWithYup from "@/hooks/useFormWithYup";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { formatISOTimeOfDay } from "@/utils/datetime";
import { muiField, muiFieldError } from "@/utils/hookForm";

import { waterIntakePageQuery } from "../WaterIntakePage";

type ModalProps = {
  visibility: boolean;
  clientId: string;
  date: string;
  currentWaterIntake: ClientWaterIntakeFragment | undefined;
  onClose: () => void;
};
const useStyles = makeStyles()((theme) => ({
  root: {
    overflowY: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(24, 24),
    backgroundColor: colors.white,
    overflowY: "auto",
    width: "470px",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(4, 3, 0, 3),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  titleText: {
    fontSize: "14px",
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "20px",
  },
  form: {
    margin: theme.spacing(4, 3, 0, 3),
  },
  fields: {
    width: "100%",
  },
  fieldContainer: {
    marginBottom: "20px",
  },
  error: {
    fontSize: theme.typography.pxToRem(12),
  },
}));

const FieldLabel = withStyles(Typography, {
  root: {
    color: colors.gray,
    fontSize: "14px",
    marginBottom: "12px",
    "& label": {
      color: "#D13F3F",
    },
  },
});

type FormValues = {
  water: string;
  time: Date;
};

export const validationSchema = yup.object().shape({
  water: yup
    .number()
    .required("Water intake is required")
    .nullable()
    .transform((value) => (isNaN(value) ? null : value))
    .test(
      "has2decimal",
      "Value must not exceed 2 decimal places",
      function (value) {
        if (!value) return true;
        if (Number.isInteger(value)) return true;
        const result = value.toString().split(".")[1]
          ? value.toString().split(".")[1].length <= 2
          : true;
        return result;
      }
    ),
});

const addClientWaterIntakeMutation = graphql(`
  mutation AddClientWaterIntake($input: CreateWaterIntakeInput!) {
    createWaterIntake(input: $input) {
      waterIntake {
        ...ClientWaterIntake
      }
    }
  }
`);

const updateClientWaterIntakeMutation = graphql(`
  mutation UpdateClientWaterIntake($input: UpdateWaterIntakeInput!) {
    updateWaterIntake(input: $input) {
      waterIntake {
        ...ClientWaterIntake
      }
    }
  }
`);

const AddWaterIntakeModal: React.FC<ModalProps> = ({
  visibility,
  onClose,
  clientId,
  date,
  currentWaterIntake,
}) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const [addClientWaterIntake] = useMutation(addClientWaterIntakeMutation);
  const [updateClientWaterIntake] = useMutation(
    updateClientWaterIntakeMutation
  );

  const defaultValues = {
    water: "",
    time: new Date(),
  };
  const { control, handleSubmit, reset } = useFormWithYup<FormValues>({
    defaultValues: defaultValues,
    validationSchema,
    reValidateMode: "onBlur",
  });

  useEffect(() => {
    if (currentWaterIntake != undefined) {
      reset({
        water: currentWaterIntake.amountMl.toString() ?? "",
        time: new Date(
          `${currentWaterIntake.drankAt} ${currentWaterIntake.drankOn}`
        ),
      });
    } else {
      reset(defaultValues);
    }
  }, [currentWaterIntake]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const values = validationSchema.cast(data);

    if (currentWaterIntake != undefined) {
      const input: UpdateWaterIntakeInput = {
        amountMl: values?.water ?? 0,
        drankAt: formatISOTimeOfDay(data.time),
        id: currentWaterIntake.id,
      };

      try {
        const result = await updateClientWaterIntake({
          variables: { input },
          refetchQueries: [
            { query: waterIntakePageQuery, variables: { clientId, date } },
          ],
        });

        if (result.data) {
          snackbar.open("Water intake updated successfully!");
        }
      } catch {
        snackbar.open("Unable to update water intake. Please try again later");
      }
      onClose();
      reset();
    } else {
      const input: CreateWaterIntakeInput = {
        amountMl: values?.water,
        drankOn: formatISO(new Date(date), { representation: "date" }),
        drankAt: formatISOTimeOfDay(data.time),
        clientId: clientId,
      };

      try {
        const result = await addClientWaterIntake({
          variables: { input },
          refetchQueries: [
            { query: waterIntakePageQuery, variables: { clientId, date } },
          ],
        });

        if (result.data) {
          snackbar.open("Water intake created successfully!");
        }
      } catch {
        snackbar.open("Unable to add water intake. Please try again later");
      }
      onClose();
      reset();
    }
  };

  return (
    <>
      <Modal open={visibility} onClose={onClose} className={classes.root}>
        <Box className={classes.modalBody}>
          <Box className={classes.modalHeader}>
            <Typography className={classes.modalHeaderText}>
              Add Water Intake
            </Typography>
            <IconButton onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <form
            className={classes.form}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box>
              <Box className={classes.fieldContainer}>
                <FieldLabel>How much did you drink?</FieldLabel>
                <Controller
                  name="water"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <TextField
                        {...muiField(field)}
                        {...muiFieldError(error)}
                        className={classes.fields}
                        type="number"
                        placeholder="E.g. 500 ml"
                      />
                    );
                  }}
                />
              </Box>
              <Box className={classes.fieldContainer}>
                <FieldLabel>Time</FieldLabel>
                <Controller
                  control={control}
                  name="time"
                  render={({
                    field: { ref, onChange, ...field },
                    fieldState,
                  }) => (
                    <MobileTimePicker
                      ref={ref}
                      onChange={(value) => onChange(value as Date)}
                      {...field}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!fieldState?.error,
                          helperText: fieldState?.error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginBottom={4}
              >
                <Button color="primary" type="submit">
                  Save
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default AddWaterIntakeModal;
